const template = document.createElement("template");
template.innerHTML = `
<style>
  :host{
    --ctl-font: var(--ctl-font-sans);
    --ctl-color-text: var(--ctl-color-text);
    --ctl-color-muted: var(--ctl-color-muted);
    --ctl-color-border: var(--ctl-color-border);
    --ctl-color-surface: var(--ctl-color-surface);
    --ctl-color-surface-2: var(--ctl-color-surface-2);
    --ctl-color-brand: var(--ctl-color-brand);
    --ctl-focus-ring: var(--ctl-focus-ring);

    display:inline-block;
    font-family: var(--ctl-font);
    color: var(--ctl-color-text);
    min-width: 0;
  }

  .root{
    display:inline-flex;
    align-items: stretch;
    border-radius: var(--_radius, 12px);
    border: 1px solid var(--ctl-color-border);
    background: var(--ctl-color-surface);
    overflow: hidden;
    user-select: none;
  }

  :host([variant="solid"]) .root{
    background: color-mix(in srgb, var(--ctl-color-border) 18%, var(--ctl-color-surface));
  }

  :host([disabled]) .root{
    opacity: .65;
    cursor: not-allowed;
  }

  .btn{
    all: unset;
    position: relative;
    cursor: pointer;
    display:inline-flex;
    align-items:center;
    justify-content:center;
    gap: 8px;
    padding: var(--_padY, 8px) var(--_padX, 12px);
    font-size: var(--_fs, 13px);
    line-height: 1.2;
    font-weight: var(--ctl-font-weight-heavy);
    color: var(--ctl-color-muted);
    min-width: 0;
    white-space: nowrap;
  }

  .btn:hover{
    background: color-mix(in srgb, var(--ctl-color-border) 26%, transparent);
  }

  .btn:focus-visible{
    outline: none;
    box-shadow: var(--ctl-focus-ring);
    z-index: 2;
  }

  .btn[aria-checked="true"]{
    color: var(--ctl-color-text);
  }

  :host([variant="outline"]) .btn[aria-checked="true"]{
    background: color-mix(in srgb, var(--ctl-color-brand) 8%, var(--ctl-color-surface));
  }

  :host([variant="solid"]) .btn[aria-checked="true"]{
    background: var(--ctl-color-surface);
    box-shadow: var(--ctl-shadow-xs);
  }

  .btn[aria-disabled="true"]{
    opacity: .55;
    cursor: not-allowed;
  }
  .btn[aria-disabled="true"]:hover{
    background: transparent;
  }

  .divider{
    width: 1px;
    background: color-mix(in srgb, var(--ctl-color-border) 85%, transparent);
  }

  :host([size="sm"]){ --_fs: 12px; --_padY: 6px; --_padX: 10px; --_radius: 10px; }
  :host([size="md"]){ --_fs: 13px; --_padY: 8px; --_padX: 12px; --_radius: 12px; }
  :host([size="lg"]){ --_fs: 14px; --_padY: 10px; --_padX: 14px; --_radius: 14px; }

  /* Hide light-dom template hooks */
  ::slotted(template[slot="item"]){ display:none; }
</style>

<div class="root" part="root" role="radiogroup" aria-disabled="false">
  <div id="items" part="items"></div>
  <slot name="item" id="itemSlot"></slot>
</div>
`;

function safeJsonParse(str, fallback) {
  try { return JSON.parse(str); } catch { return fallback; }
}
function toArray(v){ return Array.isArray(v) ? v : []; }
function escapeHtml(s) {
  return String(s ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}
function clampSize(v){ return (v === "sm" || v === "lg") ? v : "md"; }
function clampVariant(v){ return (v === "solid") ? "solid" : "outline"; }

export class CtlSegmented extends HTMLElement {
  static get observedAttributes() {
    return ["value","disabled","size","variant","items"];
  }

  constructor(){
    super();
    this.attachShadow({ mode: "open" }).appendChild(template.content.cloneNode(true));
    this._itemsWrap = this.shadowRoot.getElementById("items");
    this._slot = this.shadowRoot.getElementById("itemSlot");

    this._items = [];
    this._value = "";

    this._tmpl = null;

    // Flag to prevent infinite loops when syncing props with attributes
    this._updatingFromAttribute = false;

    this._onClick = this._onClick.bind(this);
    this._onKeyDown = this._onKeyDown.bind(this);
    this._onSlotChange = this._onSlotChange.bind(this);
  }

  connectedCallback(){
    if (!this.hasAttribute("size")) this.setAttribute("size","md");
    if (!this.hasAttribute("variant")) this.setAttribute("variant","outline");

    if (this.hasAttribute("items")) this.items = safeJsonParse(this.getAttribute("items"), []);
    if (this.hasAttribute("value")) this.value = this.getAttribute("value") || "";

    this.shadowRoot.addEventListener("click", this._onClick);
    this.shadowRoot.addEventListener("keydown", this._onKeyDown);
    this._slot.addEventListener("slotchange", this._onSlotChange);

    this._collectTemplate();
    this._render();
  }

  disconnectedCallback(){
    this.shadowRoot.removeEventListener("click", this._onClick);
    this.shadowRoot.removeEventListener("keydown", this._onKeyDown);
    this._slot.removeEventListener("slotchange", this._onSlotChange);
  }

  attributeChangedCallback(name){
    if (name === "size") {
      const v = clampSize(this.getAttribute("size"));
      if (this.getAttribute("size") !== v) this.setAttribute("size", v);
    }
    if (name === "variant") {
      const v = clampVariant(this.getAttribute("variant"));
      if (this.getAttribute("variant") !== v) this.setAttribute("variant", v);
    }
    
    // Use flag to prevent infinite loops
    this._updatingFromAttribute = true;
    try {
      if (name === "items") this.items = safeJsonParse(this.getAttribute("items"), []);
      if (name === "value") this.value = this.getAttribute("value") || "";
    } finally {
      this._updatingFromAttribute = false;
    }
    
    if (name === "disabled") this._syncDisabled();
  }

  // ---- props ----
  get items(){ return this._items; }
  set items(v){
    this._items = toArray(v).map((it) => {
      const obj = (it && typeof it === "object") ? it : {};
      const value = String(obj.value ?? "");
      const label = String(obj.label ?? value);
      const disabled = !!obj.disabled;
      const icon = obj.icon != null ? String(obj.icon) : "";
      return value ? { value, label, disabled, icon } : null;
    }).filter(Boolean);

    // Only update attribute if not already updating from attribute (prevent infinite loop)
    if (!this._updatingFromAttribute) {
      this.setAttribute("items", JSON.stringify(this._items));
    }
    
    // ensure value is valid
    if (!this._items.some((x) => x.value === this._value)) {
      const firstEnabled = this._items.find((x) => !x.disabled);
      this._value = firstEnabled ? firstEnabled.value : "";
      if (!this._updatingFromAttribute) {
        this.setAttribute("value", this._value);
      }
    }
    this._render();
  }

  get value(){ return this._value || ""; }
  set value(v){
    const next = String(v ?? "");
    this._value = next;
    // Only update attribute if not already updating from attribute (prevent infinite loop)
    if (!this._updatingFromAttribute) {
      this.setAttribute("value", next);
    }
    this._renderSelectionOnly();
  }

  get disabled(){ return this.hasAttribute("disabled"); }
  set disabled(v){ v ? this.setAttribute("disabled","") : this.removeAttribute("disabled"); }

  get size(){ return clampSize(this.getAttribute("size")); }
  set size(v){ this.setAttribute("size", clampSize(v)); }

  get variant(){ return clampVariant(this.getAttribute("variant")); }
  set variant(v){ this.setAttribute("variant", clampVariant(v)); }

  // ---- template slot ----
  _onSlotChange(){
    this._collectTemplate();
    this._render();
  }

  _collectTemplate(){
    const assigned = this._slot.assignedElements({ flatten: true }) || [];
    const tmpl = assigned.find((el) => el.tagName === "TEMPLATE") || null;
    this._tmpl = tmpl;
  }

  _renderTemplate(item){
    const html = this._tmpl.innerHTML.replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (_, key) => {
      const v = item[key];
      return escapeHtml(v == null ? "" : String(v));
    });
    return html;
  }

  // ---- render ----
  _syncDisabled(){
    const root = this.shadowRoot.querySelector(".root");
    root?.setAttribute("aria-disabled", this.disabled ? "true" : "false");
  }

  _render(){
    this._syncDisabled();
    const items = this._items;
    const value = this._value;

    const parts = [];

    items.forEach((it, idx) => {
      const checked = it.value === value;
      const ariaDisabled = (this.disabled || it.disabled) ? "true" : "false";
      const tabIndex = checked && ariaDisabled === "false" ? "0" : "-1";

      const inner = this._tmpl
        ? this._renderTemplate({ ...it, disabled: ariaDisabled })
        : `
          ${it.icon ? `<span part="icon" aria-hidden="true">${escapeHtml(it.icon)}</span>` : ""}
          <span part="label">${escapeHtml(it.label)}</span>
        `;

      parts.push(`
        <button
          class="btn"
          type="button"
          role="radio"
          aria-checked="${checked ? "true" : "false"}"
          aria-disabled="${ariaDisabled}"
          tabindex="${tabIndex}"
          data-value="${escapeHtml(it.value)}"
        >
          ${inner}
        </button>
      `);

      if (idx !== items.length - 1) parts.push(`<span class="divider" aria-hidden="true"></span>`);
    });

    this._itemsWrap.innerHTML = parts.join("");
  }

  _renderSelectionOnly(){
    // update aria-checked + tabindex without rebuilding all HTML when possible
    const btns = this.shadowRoot.querySelectorAll(".btn[data-value]");
    btns.forEach((b) => {
      const v = b.getAttribute("data-value");
      const checked = v === this._value;
      b.setAttribute("aria-checked", checked ? "true" : "false");

      const ariaDisabled = b.getAttribute("aria-disabled") === "true";
      b.tabIndex = (checked && !ariaDisabled) ? 0 : -1;
    });
  }

  // ---- interaction ----
  _onClick(e){
    const btn = e.target?.closest?.(".btn[data-value]");
    if (!btn) return;

    const ariaDisabled = btn.getAttribute("aria-disabled") === "true";
    if (this.disabled || ariaDisabled) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    const next = btn.getAttribute("data-value");
    if (!next || next === this._value) return;

    this._value = next;
    this.setAttribute("value", next);
    this._renderSelectionOnly();

    const item = this._items.find((x) => x.value === next) || null;
    this.dispatchEvent(new CustomEvent("ctl-change", {
      bubbles: true,
      composed: true,
      detail: { value: next, item }
    }));
  }

  _onKeyDown(e){
    const keys = ["ArrowLeft","ArrowRight","ArrowUp","ArrowDown","Home","End","Enter"," "];
    if (!keys.includes(e.key)) return;

    const active = this.shadowRoot.activeElement;
    const isBtn = active?.classList?.contains("btn");
    if (!isBtn) return;

    const enabled = this._items.filter((it) => !it.disabled);
    if (!enabled.length) return;

    const currentIdx = enabled.findIndex((it) => it.value === this._value);
    let nextIdx = currentIdx < 0 ? 0 : currentIdx;

    if (e.key === "Home") nextIdx = 0;
    if (e.key === "End") nextIdx = enabled.length - 1;

    if (e.key === "ArrowLeft" || e.key === "ArrowUp") nextIdx = Math.max(0, nextIdx - 1);
    if (e.key === "ArrowRight" || e.key === "ArrowDown") nextIdx = Math.min(enabled.length - 1, nextIdx + 1);

    if (e.key === "Enter" || e.key === " ") {
      // already selected by roving tabindex; just prevent scroll
      e.preventDefault();
      return;
    }

    e.preventDefault();
    const next = enabled[nextIdx]?.value;
    if (!next || next === this._value) {
      // move focus anyway
      const b = this.shadowRoot.querySelector(`.btn[data-value="${CSS.escape(this._value)}"]`);
      b?.focus();
      return;
    }

    this._value = next;
    this.setAttribute("value", next);
    this._renderSelectionOnly();

    const b = this.shadowRoot.querySelector(`.btn[data-value="${CSS.escape(next)}"]`);
    b?.focus();

    const item = this._items.find((x) => x.value === next) || null;
    this.dispatchEvent(new CustomEvent("ctl-change", {
      bubbles: true,
      composed: true,
      detail: { value: next, item }
    }));
  }
}

customElements.define("ctl-segmented", CtlSegmented);
